// src/lib/tenant.ts
// Tenant detection for the two ways a subscriber's public site is reached:
//  1. Path handle on the platform domain — launchkitnaija.com/s/<handle>
//  2. A whitelabel client's own custom domain (the whole host is theirs)
// The main platform (dashboard, marketing, funnels) is served from the
// root of launchkitnaija.com.

export const ROOT_DOMAIN = (
  process.env.REACT_APP_ROOT_DOMAIN || "launchkitnaija.com"
).toLowerCase();

// The path prefix under which every subscriber site lives.
export const SITE_PREFIX = "s";

// Hosts that are the platform itself (never a custom-domain tenant)
const PLATFORM_HOSTS = new Set([
  ROOT_DOMAIN,
  `www.${ROOT_DOMAIN}`,
  `app.${ROOT_DOMAIN}`,
  "localhost",
  "127.0.0.1",
]);

/**
 * If the app is loaded on a whitelabel custom domain, return that host so
 * the tenant is resolved by host. Returns null on the platform domain
 * (where tenants are addressed by /s/<handle> instead).
 */
export const getCustomDomainHost = (): string | null => {
  const host = window.location.hostname.toLowerCase();
  if (PLATFORM_HOSTS.has(host)) return null;
  if (/^[\d.]+$/.test(host)) return null; // raw IP
  if (host.endsWith(".vercel.app")) return null; // preview deploys
  if (host.endsWith(".localhost")) return null; // local dev
  return host; // a real custom domain
};

/**
 * Extract the subscriber handle from a /s/<handle>[/...] path, or null.
 */
export const getHandleFromPath = (pathname: string): string | null => {
  const parts = pathname.replace(/^\/+/, "").split("/");
  if (parts[0] === SITE_PREFIX && parts[1]) {
    return parts[1].toLowerCase();
  }
  return null;
};

/** Public URL for a subscriber's site given their handle. */
export const siteUrlForHandle = (handle?: string | null): string | null =>
  handle ? `https://${ROOT_DOMAIN}/${SITE_PREFIX}/${handle}` : null;
